
import { useState } from "react";
import {
  Sidebar,
  SidebarContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarGroup,
  SidebarGroupContent,
} from "@/components/ui/sidebar";
import { 
  Users, 
  FileText, 
  Settings, 
  Share2, 
  ChevronRight,
  Calendar,
  DollarSign,
  CheckSquare,
  Instagram,
  Youtube,
  Facebook,
  Newspaper,
  Globe,
  HelpCircle,
  Briefcase,
  MessageSquare,
  Route
} from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

interface AdminSidebarProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
}

export function AdminSidebar({ activeTab, onTabChange }: AdminSidebarProps) {
  const [openSections, setOpenSections] = useState<string[]>(['conteudos', 'site', 'administrativo', 'redes']);

  const toggleSection = (section: string) => {
    setOpenSections(prev => 
      prev.includes(section) 
        ? prev.filter(s => s !== section)
        : [...prev, section]
    );
  };

  const menuItems = [
    {
      id: 'pacientes',
      title: 'Pacientes',
      icon: Users,
      action: () => onTabChange('pacientes')
    },
    {
      id: 'conteudos',
      title: 'Conteúdos',
      icon: FileText,
      submenu: [
        {
          id: 'imprensa',
          title: 'Imprensa',
          icon: Newspaper,
          action: () => onTabChange('imprensa')
        },
        {
          id: 'blog',
          title: 'Blog',
          icon: FileText,
          action: () => onTabChange('blog')
        }
      ]
    },
    {
      id: 'site',
      title: 'Site',
      icon: Globe,
      submenu: [
        {
          id: 'servicos',
          title: 'Página de Serviços',
          icon: Briefcase,
          action: () => onTabChange('servicos')
        },
        {
          id: 'depoimentos',
          title: 'Depoimentos',
          icon: MessageSquare,
          action: () => onTabChange('depoimentos')
        },
        {
          id: 'jornada',
          title: 'Jornada do Paciente',
          icon: Route,
          action: () => onTabChange('jornada')
        },
        {
          id: 'historia',
          title: 'Nossa História',
          icon: FileText,
          action: () => onTabChange('historia')
        },
        {
          id: 'faq',
          title: 'FAQ',
          icon: HelpCircle,
          action: () => onTabChange('faq')
        },
        {
          id: 'configuracoes',
          title: 'Configurações',
          icon: Settings,
          action: () => onTabChange('configuracoes')
        }
      ]
    },
    {
      id: 'administrativo',
      title: 'Administrativo',
      icon: Settings,
      submenu: [
        {
          id: 'tarefas',
          title: 'Tarefas',
          icon: CheckSquare,
          action: () => onTabChange('tarefas')
        },
        {
          id: 'financeiro',
          title: 'Financeiro',
          icon: DollarSign,
          action: () => onTabChange('financeiro')
        },
        {
          id: 'calendario',
          title: 'Calendário',
          icon: Calendar,
          action: () => onTabChange('calendario')
        }
      ]
    },
    {
      id: 'redes',
      title: 'Redes Sociais',
      icon: Share2,
      submenu: [
        {
          id: 'instagram',
          title: 'Instagram',
          icon: Instagram,
          action: () => window.open('https://business.instagram.com/', '_blank')
        },
        {
          id: 'youtube',
          title: 'YouTube',
          icon: Youtube,
          action: () => window.open('https://studio.youtube.com/', '_blank')
        },
        {
          id: 'facebook',
          title: 'Facebook',
          icon: Facebook,
          action: () => window.open('https://business.facebook.com/', '_blank')
        }
      ]
    }
  ];

  return (
    <Sidebar className="w-64">
      <SidebarHeader className="p-4 mt-5">
        <h2 className="text-lg font-semibold text-gray-800">Painel Admin</h2>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {menuItems.map((item) => (
                <SidebarMenuItem key={item.id}>
                  {item.submenu ? (
                    <Collapsible 
                      open={openSections.includes(item.id)}
                      onOpenChange={() => toggleSection(item.id)}
                    >
                      <CollapsibleTrigger asChild>
                        <SidebarMenuButton className="w-full justify-between">
                          <div className="flex items-center gap-2">
                            <item.icon className="w-4 h-4" />
                            <span>{item.title}</span>
                          </div>
                          <ChevronRight 
                            className={`w-4 h-4 transition-transform ${
                              openSections.includes(item.id) ? 'rotate-90' : ''
                            }`} 
                          />
                        </SidebarMenuButton>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <SidebarMenuSub>
                          {item.submenu.map((subItem) => (
                            <SidebarMenuSubItem key={subItem.id}>
                              <SidebarMenuSubButton
                                onClick={subItem.action}
                                isActive={activeTab === subItem.id}
                                className="cursor-pointer"
                              >
                                <subItem.icon className="w-4 h-4" />
                                <span>{subItem.title}</span>
                              </SidebarMenuSubButton>
                            </SidebarMenuSubItem>
                          ))}
                        </SidebarMenuSub>
                      </CollapsibleContent>
                    </Collapsible>
                  ) : (
                    <SidebarMenuButton
                      onClick={item.action}
                      isActive={activeTab === item.id}
                      className="cursor-pointer"
                    >
                      <item.icon className="w-4 h-4" />
                      <span>{item.title}</span>
                    </SidebarMenuButton>
                  )}
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </Sidebar>
  );
}
